'use client';

import type { RefObject } from 'react';

import { useCallback } from 'react';

import { useRouter, usePathname } from 'src/routes/hooks';

import type { GuideSearchItem } from '../../utils/guide-search';

/**
 * 검색 결과 하나를 골랐을 때 — 그 매뉴얼(또는 그 절)로 간다.
 * 🔑 같은 매뉴얼 안의 절이면 주소의 `#` 만 바꾼다 — `hashchange` 가 나야 접힌 칸(세부 기능)이 열린다.
 * @param onDone 고른 뒤 할 일(검색칸 비우기 등)
 */
export function useGuideSearchPickHandler(onDone?: () => void) {
  const router = useRouter();
  const pathname = usePathname();
  return useCallback(
    (item: GuideSearchItem | null) => {
      if (!item) return;
      const [path, hash] = item.href.split('#');
      if (hash && path === pathname) window.location.hash = hash;
      else router.push(item.href);
      onDone?.();
    },
    [router, pathname, onDone]
  );
}

/**
 * 「검색」 단추 — 맨 위 결과로 간다. 결과가 없으면 아무 일도 하지 않는다(칸 아래 「찾는 가이드가 없어요」가 선다).
 * @param results 지금 결과 줄
 * @param pick `useGuideSearchPickHandler` 가 돌려준 것
 */
export function useGuideSearchSubmitHandler(results: readonly GuideSearchItem[], pick: (item: GuideSearchItem | null) => void) {
  return useCallback(() => pick(results[0] ?? null), [results, pick]);
}

/**
 * 「많이 찾는 키워드」 칩 — 그 말로 찾고 검색칸에 포커스를 둔다(결과 줄이 칸 아래 펼쳐지고 화살표로 고를 수 있게).
 * @param run `useGuideSearchModel().run`
 * @param inputRef 검색칸의 입력 요소
 */
export function useGuideSearchKeywordHandler(run: (word: string) => void, inputRef: RefObject<HTMLInputElement | null>) {
  return useCallback(
    (word: string) => {
      run(word);
      inputRef.current?.focus();
    },
    [run, inputRef]
  );
}
